// Slider component for rating selections.
import { Center, HorizBox, Pad, PadBox } from 'component/basics';
import {
    colorBorderPrimary,
    colorButtonAccent,
    colorSurfacePrimary,
    colorSurfaceSecondary,
    colorTextOnAccentColorBg,
    colorTextPrimary,
} from 'component/color';
import { UtilityText } from 'component/text';
import { NonSelectableView } from 'platform-specific/non-selectable-view';
import { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { SliderWithStopPoints } from 'system/sliderwithstoppoints';
import type { TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';

import { FadingView } from './animation';

export type RatingSelectorProps = {
    value?: number;
    defaultValue?: number | null;
    optionLabels: TLabel[];
    optionColors?: string[];
    placeholder?: string;
    onChangeValue: (value: number) => void;
};

// The rating selecton from Comment Slider.
// Figma: https://www.figma.com/design/SgBNOg9PPIKIsSMAmU32rK/Comments-Slider---Testing?node-id=648-44526&m=dev

export function RatingSelector({ value, defaultValue, optionLabels, optionColors, placeholder, onChangeValue }: RatingSelectorProps) {
    const s = RatingSelectorStyle;
    const [dragging, setDragging] = useState(false);

    return (
        <NonSelectableView>
            <View style={s.outer}>
                <SliderWithStopPoints
                    isDefault={!value && !!defaultValue}
                    numberOfStops={5}
                    onChangeValue={onChangeValue}
                    onEndDragging={() => {
                        setDragging(false);
                    }}
                    onStartDragging={() => {
                        setDragging(true);
                    }}
                    optionColors={optionColors}
                    optionLabels={optionLabels}
                    title={placeholder}
                    value={value ?? defaultValue ?? 0}
                />
                <Pad size={16} />
                <RatingLabelBar interacting={dragging} optionLabels={optionLabels} value={value} />
            </View>
        </NonSelectableView>
    );
}

const RatingSelectorStyle = StyleSheet.create({
    leftLabel: {
        position: 'absolute',
        left: 20,
        width: '25%',
        flexShrink: 1,
    },
    rightLabel: {
        position: 'absolute',
        right: 20,
        width: '25%',
        flexShrink: 1,
    },
    centerLabel: {
        position: 'absolute',
        left: 0,
        right: 0,
        alignItems: 'center',
    },
    outer: {
        borderRadius: 8,
    },
    slider: {
        paddingTop: 26,
        marginBottom: 0,
        paddingBottom: 20,
        paddingHorizontal: 22,
    },
    bar: {
        position: 'absolute',
        backgroundColor: colorBorderPrimary,
        height: 8,
        borderRadius: 4,
        zIndex: -1,
        left: 20,
        right: 20,
        top: 25,
    },
    toggleBall: {
        position: 'absolute',
        backgroundColor: colorSurfacePrimary,
        width: 32,
        height: 32,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: colorSurfaceSecondary,
        boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.30)' as any, // FIXME: boxShadow not typed for native
        zIndex: 1,
        left: 20,
        top: 16,
    },
    selectionStopPoint: {
        width: 6,
        height: 6,
        borderRadius: 4,
        backgroundColor: colorSurfacePrimary,
        borderLeftColor: colorBorderPrimary,
        borderLeftWidth: 2,
        borderColor: colorSurfaceSecondary,
    },
});

const RatingLegendStyle = StyleSheet.create({
    ratingLegendContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        height: 48,
    },
    labelContainer: {
        flex: 1,
        position: 'absolute',
        alignItems: 'center',
        overflow: 'visible',
        transform: [{ translateX: '-50%' }],
    },
    labelContainerLast: {
        flex: 0,
        alignItems: 'flex-end',
        overflow: 'visible',
        transform: [{ translateX: '-100%' }],
    },
    labelContainerFirst: {
        alignItems: 'flex-start',
        transform: [],
    },
    youLabel: {
        paddingHorizontal: 8,
        backgroundColor: colorButtonAccent,
        height: 20,
        justifyContent: 'center',
        borderRadius: 100,
    },
});

export type RatingLabelBarProps = { value: any; optionLabels: any; interacting?: boolean };

export function RatingLabelBar({ value, optionLabels, interacting = false }: RatingLabelBarProps) {
    const countLabels = optionLabels.length;

    return (
        <View>
            <FadingView duration={200} style={RatingLegendStyle.ratingLegendContainer} visible={!value || interacting}>
                {optionLabels.map((label, i) => {
                    const isFirst = i === 0;
                    const isLast = i === countLabels - 1;

                    if (i % 2 !== 0) return null; // skip every second

                    return (
                        <View key={i}>
                            <View
                                style={[
                                    RatingLegendStyle.labelContainer,
                                    isLast && RatingLegendStyle.labelContainerLast,
                                    isFirst && RatingLegendStyle.labelContainerFirst,
                                ]}
                            >
                                <RatingLabel label={t(label)} selected={false} />
                            </View>
                        </View>
                    );
                })}
            </FadingView>
            <FadingView
                duration={200}
                style={[RatingLegendStyle.ratingLegendContainer, { position: 'absolute', width: '100%' }]}
                visible={value && !interacting}
            >
                {optionLabels.map((label, i) => {
                    const isFirst = i === 0;
                    const isLast = i === countLabels - 1;

                    return (
                        <View key={i}>
                            <View
                                style={[
                                    RatingLegendStyle.labelContainer,
                                    isLast && RatingLegendStyle.labelContainerLast,
                                    isFirst && RatingLegendStyle.labelContainerFirst,
                                ]}
                            >
                                {value - 1 === i && <RatingLabel label={t(label)} selected={value} />}
                            </View>
                        </View>
                    );
                })}
            </FadingView>
        </View>
    );
}

function RatingLabel({ label, selected }) {
    return (
        <>
            <UtilityText
                color={selected ? colorButtonAccent : colorTextPrimary}
                label={t(label)}
                numberOfLines={1}
                type='tiny'
                weight='medium'
            />
            <Pad size={4} />
            <View style={[RatingLegendStyle.youLabel, { opacity: selected ? 1 : 0 }]}>
                <UtilityText color={colorTextOnAccentColorBg} label={t('You')} type='tiny' weight='medium' />
            </View>
        </>
    );
}
